import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsAndConditions from "./TermsAndConditions";

const UserConsentAgreement = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [legalModalView, setLegalModalView] = useState(null); // null, "privacy", or "terms"
  const [isDark, setIsDark] = useState(
    document.documentElement.getAttribute("data-theme") === "dark",
  );

  useEffect(() => {
    const handleResize = () => {
      setIsSmallMobile(window.innerWidth <= 480);
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Check initial size
    window.addEventListener("resize", handleResize);

    // Watch for theme changes via data-theme attribute
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, []);

  const containerStyle = {
    fontFamily: "Arial, Helvetica, sans-serif",
    fontWeight: "400",
    fontSize: "1rem",
    lineHeight: "1.8",
    color: isDark ? "#e0e0e0" : "#2c2c2c",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: isSmallMobile ? "8px" : isMobile ? "15px" : "40px",
    backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
    minHeight: "100vh",
  };

  const contentStyle = {
    backgroundColor: isDark ? "#2a2a2a" : "white",
    padding: isSmallMobile ? "15px" : isMobile ? "25px" : "60px",
    border: isDark ? "1px solid #444" : "1px solid #e0e0e0",
    boxShadow: isDark
      ? "0 4px 6px rgba(0,0,0,0.3)"
      : "0 4px 6px rgba(0,0,0,0.05)",
  };

  const boxStyle = {
    backgroundColor: isDark ? "#333" : "#f8f8f8",
    padding: isSmallMobile ? "10px" : isMobile ? "15px" : "25px",
    border: isDark ? "1px solid #555" : "1px solid #ddd",
    marginBottom: isSmallMobile ? "20px" : isMobile ? "30px" : "40px",
  };

  const hrStyle = {
    border: "none",
    height: "2px",
    backgroundColor: isDark ? "#444" : "#ecf0f1",
    margin: "30px 0",
  };

  const linkButtonStyle = {
    background: "none",
    border: "none",
    color: isDark ? "#90caf9" : "#333",
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
    font: "inherit",
  };

  const footerStyle = {
    textAlign: "center",
    fontStyle: "italic",
    color: isDark ? "#999" : "#666",
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: isDark ? "1px solid #555" : "1px solid #ddd",
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <div
          style={{
            textAlign: "center",
            padding: "20px 0",
            marginBottom: "20px",
          }}
        >
          <img
            src="/KinShieldIcon.png"
            alt="KinShield Logo"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              maxWidth: "100px",
              height: "auto",
              mixBlendMode: isDark ? "normal" : "multiply",
              borderRadius: "15px",
              boxShadow: isDark
                ? "0 6px 16px rgba(0, 0, 0, 0.5)"
                : "0 6px 16px rgba(0, 0, 0, 0.3)",
              backgroundColor: isDark ? "#ffffff" : "transparent",
              padding: isDark ? "4px" : "0",
              WebkitUserDrag: "none",
              userSelect: "none",
            }}
          />
        </div>
        <style>
          {`
            .consent-agreement ul,
            .consent-agreement ol {
              text-align: left !important;
              padding-left: 30px !important;
              margin: 20px 0 !important;
            }
            .consent-agreement li {
              text-align: left !important;
              margin: 12px 0 !important;
              line-height: 1.6;
            }
            .consent-agreement h1, .consent-agreement h2, .consent-agreement h3, .consent-agreement h4 {
              text-align: left !important;
              font-family: Arial, Helvetica, sans-serif;
              font-weight: 400;
              margin-top: 40px;
              margin-bottom: 20px;
            }
            .consent-agreement h1 {
              font-size: 2.2em;
              margin-bottom: 30px;
              border-bottom: 2px solid ${isDark ? "#666" : "#333"};
              padding-bottom: 15px;
            }
            .consent-agreement h2 {
              font-size: 1.5em;
              margin-top: 50px;
              border-bottom: 1px solid ${isDark ? "#555" : "#ccc"};
              padding-bottom: 10px;
            }
            .consent-agreement h3 {
              font-size: 1.2em;
              margin-top: 35px;
            }
            .consent-agreement h4 {
              font-size: 1.1em;
              margin-top: 25px;
            }
            .consent-agreement p {
              margin: 16px 0;
              text-align: justify;
            }
            .consent-agreement table {
              border-collapse: collapse;
              width: 100%;
              margin: 25px 0;
            }
            .consent-agreement th {
              background-color: ${isDark ? "#333" : "#f5f5f5"};
              color: ${isDark ? "#e0e0e0" : "#333"};
              font-family: Arial, Helvetica, sans-serif;
              font-weight: 400;
              text-align: left;
              padding: 15px;
              border: 1px solid ${isDark ? "#555" : "#ddd"};
            }
            .consent-agreement td {
              padding: 12px 15px;
              border: 1px solid ${isDark ? "#555" : "#ddd"};
              vertical-align: top;
            }
            .consent-agreement tr:nth-child(even) {
              background-color: ${isDark ? "#2a2a2a" : "#fafafa"};
            }
            
            /* Mobile Responsive Styles */
            @media (max-width: 768px) {
              .consent-agreement h1 {
                font-size: 1.8em !important;
                margin-top: 20px !important;
                margin-bottom: 15px !important;
                padding-bottom: 10px !important;
              }
              .consent-agreement h2 {
                font-size: 1.3em !important;
                margin-top: 25px !important;
                margin-bottom: 10px !important;
                padding-bottom: 8px !important;
              }
              .consent-agreement h3 {
                font-size: 1.1em !important;
                margin-top: 20px !important;
                margin-bottom: 8px !important;
              }
              .consent-agreement h4 {
                font-size: 1.05em !important;
                margin-top: 15px !important;
                margin-bottom: 8px !important;
              }
              .consent-agreement p {
                margin: 8px 0 !important;
              }
              .consent-agreement ul,
              .consent-agreement ol {
                padding-left: 20px !important;
                margin: 10px 0 !important;
              }
              .consent-agreement li {
                margin: 6px 0 !important;
              }
              .consent-agreement table {
                margin: 15px 0 !important;
                font-size: 0.9em;
              }
              .consent-agreement th,
              .consent-agreement td {
                padding: 8px 6px !important;
              }
            }
            
            @media (max-width: 480px) {
              .consent-agreement h1 {
                font-size: 1.6em !important;
                margin-top: 15px !important;
                margin-bottom: 12px !important;
              }
              .consent-agreement h2 {
                font-size: 1.2em !important;
                margin-top: 20px !important;
                margin-bottom: 8px !important;
              }
              .consent-agreement h3 {
                font-size: 1.05em !important;
                margin-top: 15px !important;
                margin-bottom: 6px !important;
              }
              .consent-agreement h4 {
                font-size: 1em !important;
                margin-top: 12px !important;
                margin-bottom: 6px !important;
              }
              .consent-agreement p {
                margin: 6px 0 !important;
              }
              .consent-agreement ul,
              .consent-agreement ol {
                padding-left: 15px !important;
                margin: 8px 0 !important;
              }
              .consent-agreement li {
                margin: 4px 0 !important;
              }
              .consent-agreement table {
                margin: 10px 0 !important;
                font-size: 0.85em;
              }
              .consent-agreement th,
              .consent-agreement td {
                padding: 6px 4px !important;
              }
            }
          `}
        </style>
        <div className="consent-agreement">
          <h1>KinShield Training Service - User Consent Agreement</h1>

          <div style={boxStyle}>
            <p style={{ textAlign: "center", marginBottom: "10px" }}>
              <strong>A service of Reliable IT Associates, LLC</strong>
            </p>
            <p
              style={{
                textAlign: "center",
                fontSize: "0.9em",
                color: isDark ? "#aaa" : "#666",
              }}
            >
              <strong>Effective Date:</strong> August 1, 2025
              <br />
              <strong>Last Updated:</strong> June 26, 2026
            </p>
          </div>

          <div style={boxStyle}>
            <h2>Important: Please Read This Document Carefully</h2>
            <p>
              This consent agreement outlines your participation in KinShield's
              cybersecurity training program and your use of KinShield's scam
              detection and analysis services. By providing your consent, you
              are agreeing to receive realistic simulated cyber attacks for
              educational purposes and to use KinShield's AI-powered analysis
              tools subject to the terms and limitations described herein.
            </p>
          </div>

          <section style={{ marginBottom: "30px" }}>
            <h2>What Is KinShield?</h2>
            <p>
              <strong>KinShield</strong> is an AI-powered cybersecurity
              education and scam awareness platform operated by Reliable IT
              Associates, LLC. KinShield sends subscribers realistic simulations
              of phishing emails, fraudulent text messages, fake social media
              scams, voice calls, and other common online threats. KinShield
              also provides AI-powered analysis tools that allow users to submit
              emails, text messages, images, screenshots, social media content,
              URLs, and phone numbers for scam detection analysis. Additionally,
              KinShield offers an AI-powered Digital Personal Guard (DPG)
              chatbot for interactive scam consultation, a fraud detection
              pipeline for document analysis, and training modules with quizzes,
              badges, and certificates.
            </p>
            <p>
              Our goal is to help you and your loved ones recognize and avoid
              real cyber attacks through hands-on experience in a safe,
              controlled environment, and to educate users about the mechanisms
              and tactics that scammers use.
            </p>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>What You're Consenting To</h2>
            <p>
              By agreeing to this consent form, you understand and agree that:
            </p>

            <h3>Simulated Attacks You Will Receive</h3>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                <strong>Phishing emails</strong> that may appear to come from
                banks, employers, government agencies, or other trusted sources
              </li>
              <li>
                <strong>Fraudulent text messages</strong> including fake
                security alerts, prize notifications, or urgent requests
              </li>
              <li>
                <strong>Social media scams</strong> via platforms you use (if
                you provide access)
              </li>
              <li>
                <strong>Voice calls</strong> with recorded messages simulating
                common phone scams
              </li>
              <li>
                <strong>Other digital deception techniques</strong> designed to
                test your awareness
              </li>
            </ul>

            <h3>Levels of Sophistication</h3>
            <p>You will receive simulations at varying levels of realism:</p>

            <h4>Level 1 (Basic):</h4>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Contains obvious indicators like misspellings, poor grammar, or
                generic messaging
              </li>
              <li>Easily identifiable as suspicious to most users</li>
            </ul>

            <h4>Level 2 (Realistic):</h4>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Professional appearance with proper branding, formatting, and
                messaging
              </li>
              <li>
                May include logos, legitimate-looking sender addresses, and
                convincing language
              </li>
              <li>Requires careful examination to identify as fraudulent</li>
            </ul>

            <h4>Level 3 (Highly Sophisticated):</h4>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Uses real company names, phone numbers, and contact information
                where legally permissible
              </li>
              <li>
                May reference actual people, recent news events, or personal
                information you've provided
              </li>
              <li>
                Designed to be extremely convincing and difficult to distinguish
                from legitimate communications
              </li>
            </ul>

            <h3>Personal Information Usage</h3>
            <p>
              To create realistic simulations and provide effective scam
              analysis, we may use:
            </p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Your name, email address, phone number, and other contact
                information
              </li>
              <li>
                Details about your workplace, interests, or personal
                circumstances that you provide
              </li>
              <li>
                Publicly available information about organizations you're
                affiliated with
              </li>
              <li>
                Information about your communication patterns and preferences
              </li>
              <li>
                Emails, text messages, images, screenshots, social media
                content, URLs, phone numbers, and documents you submit for
                analysis
              </li>
              <li>
                Browser fingerprint data (including browser type, OS, device
                identifiers, screen resolution, and other device
                characteristics) for security and fraud prevention
              </li>
              <li>
                Your chatbot conversations with the Digital Personal Guard (DPG)
              </li>
              <li>
                Your training progress, quiz scores, simulation performance, and
                risk scores
              </li>
            </ul>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>How Your Data Is Protected</h2>

            <h3>Security Measures</h3>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                All personal data is encrypted both in transit and at rest
              </li>
              <li>
                Access to your information is limited to authorized personnel
                only
              </li>
              <li>
                We implement industry-standard security controls and regular
                security audits
              </li>
              <li>
                Simulated attacks are clearly tagged in our systems to prevent
                confusion with real threats
              </li>
              <li>
                Uploaded files are scanned for viruses and malware before
                processing
              </li>
              <li>
                PII (Personally Identifiable Information) is detected and
                redacted before content is sent to third-party AI providers for
                analysis
              </li>
              <li>
                Browser fingerprinting is used for device identification and
                account security, not for advertising tracking
              </li>
              <li>
                Rate limiting and abuse prevention mechanisms protect against
                unauthorized access
              </li>
            </ul>

            <h3>Data Retention</h3>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Personal information is retained only as long as your
                subscription is active
              </li>
              <li>
                Training results and performance data may be anonymized and
                retained for service improvement
              </li>
              <li>You may request deletion of your data at any time</li>
            </ul>

            <h3>Third-Party Sharing</h3>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                We do not sell or share your personal information with third
                parties for marketing purposes
              </li>
              <li>
                User-submitted content may be processed by third-party AI
                providers (such as Anthropic and OpenAI) for scam analysis and
                detection. PII is redacted before processing where applicable.
              </li>
              <li>
                Payment information is processed by Stripe, a PCI-compliant
                payment processor
              </li>
              <li>SMS and phone services are provided by Twilio</li>
              <li>Email delivery is provided by SendGrid</li>
              <li>
                URLs may be checked against Google Safe Browsing API and
                PhishTank databases
              </li>
              <li>
                Phone numbers may be validated through IPQualityScore and other
                validation providers
              </li>
              <li>
                We may share anonymized, de-identified fraud intelligence
                (scam-side artifacts only, never victim PII) with law
                enforcement, government entities, and fraud prevention
                organizations
              </li>
              <li>
                Legal compliance may require disclosure in specific
                circumstances as outlined in our Privacy Policy
              </li>
            </ul>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Your Rights and Controls</h2>

            <h3>Participation Control</h3>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                You can pause or stop simulations at any time through your
                account settings
              </li>
              <li>
                You can adjust the frequency and sophistication level of
                simulations
              </li>
              <li>
                You can exclude specific types of attacks (e.g., voice calls
                only during business hours)
              </li>
            </ul>

            <h3>Reporting and Feedback</h3>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                You'll receive detailed reports on your performance and
                improvement over time
              </li>
              <li>
                Training materials will be provided after each simulation to
                help you learn
              </li>
              <li>
                You can provide feedback on simulations to improve the service
              </li>
            </ul>

            <h3>Withdrawal of Consent</h3>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                You may withdraw your consent and cancel your subscription at
                any time
              </li>
              <li>
                Upon cancellation, we will stop all simulations immediately
              </li>
              <li>
                You may request deletion of your personal data within 30 days of
                cancellation
              </li>
            </ul>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Important Acknowledgments</h2>

            <h3>Understanding of Realistic Nature</h3>
            <p>I understand that:</p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Simulations are designed to be convincing and may cause
                temporary concern or confusion
              </li>
              <li>
                I should treat all suspicious communications as potentially real
                threats until verified
              </li>
              <li>
                KinShield simulations will include clear identification when
                revealed as training exercises
              </li>
            </ul>

            <h3>Emotional and Psychological Considerations</h3>
            <p>I acknowledge that:</p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Receiving realistic scam attempts may cause stress, anxiety, or
                other emotional responses
              </li>
              <li>
                I am participating voluntarily and can stop at any time if the
                training becomes distressing
              </li>
              <li>
                KinShield is not responsible for emotional distress caused by
                realistic simulations
              </li>
            </ul>

            <h3>Technical Requirements</h3>
            <p>I confirm that:</p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                I have the technical ability to receive and respond to various
                types of digital communications
              </li>
              <li>
                I understand how to report suspicious communications through
                proper channels
              </li>
              <li>
                I will not forward or share KinShield simulations as if they
                were real threats
              </li>
            </ul>

            <h3>Legal Compliance</h3>
            <p>I understand that:</p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                This service is for legitimate cybersecurity training and
                educational purposes only
              </li>
              <li>
                I will not use knowledge gained to create actual phishing or
                scam attempts
              </li>
              <li>
                KinShield operates within applicable laws and regulations
                regarding simulated cyber attacks
              </li>
            </ul>

            <h3>Submitting Content for Analysis</h3>
            <p>I understand and agree that:</p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                When I forward an email or submit other content (messages,
                images, screenshots, documents, files, URLs, or phone numbers)
                for analysis, that content is processed by KinShield and sent to
                third-party AI providers (such as Anthropic and OpenAI), who
                treat it as confidential and do not use it to train their models
              </li>
              <li>
                I will only submit content that I am authorized to share, and my
                submission will not violate any law, confidentiality obligation,
                attorney-client privilege, or the policies of my employer or
                organization
              </li>
              <li>
                Unless a separate written agreement (such as a Business
                Associate Agreement) is in place, I will NOT submit protected
                health information (HIPAA), payment card data, student records
                (FERPA), export-controlled data, legally privileged material, or
                other regulated or special-category information
              </li>
              <li>
                Forwarded content may contain personal information about third
                parties, and I am responsible for having any rights or consents
                needed to submit it
              </li>
              <li>
                KinShield applies automated redaction of certain personal
                information before AI processing, but this does not guarantee
                removal of all sensitive content and does not relieve me of my
                responsibilities above
              </li>
            </ul>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Artificial Intelligence (AI) Disclosure and Limitations</h2>

            <div style={boxStyle}>
              <strong>IMPORTANT AI DISCLOSURE:</strong>
              <p>
                KinShield uses artificial intelligence (AI) and machine learning
                technologies, including large language models (LLMs) and
                computer vision models, provided by third-party AI service
                providers, to analyze user-submitted content, generate training
                simulations, provide scam detection analyses, power the Digital
                Personal Guard chatbot, and create educational content. By using
                KinShield, you acknowledge and agree to the following:
              </p>
            </div>

            <h3>AI Can Make Mistakes</h3>
            <p>
              AI-generated analyses, recommendations, assessments, and
              educational content may contain errors, inaccuracies, omissions,
              or misleading information. AI technology is inherently
              probabilistic and does not guarantee correct, complete, or
              reliable results. AI models may also produce "hallucinations" —
              plausible-sounding but factually incorrect information.
            </p>

            <h3>AI May Use Outdated Information</h3>
            <p>
              AI models are trained on data up to a certain date and may not
              reflect the most current scam tactics, fraud techniques, legal
              requirements, or real-world developments. Scam methodologies
              evolve continuously and AI models may not recognize the latest or
              most sophisticated threats.
            </p>

            <h3>AI May Produce False Results</h3>
            <p>
              AI analysis may incorrectly flag legitimate communications as
              scams (false positives) or fail to identify actual scam attempts
              (false negatives). Users should never rely solely on KinShield's
              AI analysis to determine the legitimacy of any communication,
              transaction, or interaction. Independent verification is always
              recommended.
            </p>

            <h3>AI Is a Tool, Not a Substitute for Judgment</h3>
            <p>
              AI-generated results are intended to assist and educate users, not
              to replace the user's own critical thinking, judgment, or
              professional consultation. You are ultimately responsible for your
              own decisions and actions.
            </p>

            <h3>Third-Party AI Providers</h3>
            <p>
              User-submitted content may be processed by third-party AI service
              providers. KinShield uses PII detection and redaction technology
              to protect personally identifiable information before AI
              processing where applicable, but cannot guarantee that all
              personal information will be successfully identified and redacted
              in every case.
            </p>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Not Law Enforcement or Professional Advice</h2>

            <div style={boxStyle}>
              <strong>CRITICAL NOTICE — PLEASE READ CAREFULLY:</strong>
              <p>I understand and acknowledge that:</p>
            </div>

            <h3>KinShield Is NOT Law Enforcement</h3>
            <p>
              KinShield is not a law enforcement agency, government entity, or
              regulatory body. KinShield does not have the authority, ability,
              or obligation to investigate crimes, apprehend criminals, enforce
              laws, or take any law enforcement action. If I believe I am the
              victim of a crime, I should contact local law enforcement, the
              Federal Trade Commission (FTC), the FBI Internet Crime Complaint
              Center (IC3), or other appropriate authorities immediately.
            </p>

            <h3>KinShield Does NOT Provide Legal Advice</h3>
            <p>
              Nothing provided by KinShield constitutes legal advice, legal
              counsel, or legal representation. If I require legal advice, I
              will consult a qualified attorney licensed in my jurisdiction.
            </p>

            <h3>KinShield Does NOT Provide Financial Advice</h3>
            <p>
              Nothing provided by KinShield constitutes financial advice,
              investment advice, or tax advice. If I need financial guidance, I
              will consult a qualified financial professional.
            </p>

            <h3>KinShield Does NOT Provide Personal or Medical Advice</h3>
            <p>
              KinShield does not provide personal counseling, psychological
              advice, or medical advice of any kind.
            </p>

            <h3>KinShield Is Solely Educational</h3>
            <div style={boxStyle}>
              <strong>
                THE SOLE AND EXCLUSIVE PURPOSE OF KINSHIELD IS TO EDUCATE USERS
                ABOUT THE MECHANISMS AND TACTICS THAT SCAMMERS USE TO HELP USERS
                SPOT AND AVOID SCAMS.
              </strong>
              <p>
                All analyses, assessments, alerts, training materials,
                simulations, chatbot conversations, and all other Service
                outputs are provided exclusively for educational and
                informational purposes. They are not intended to serve as, and
                should not be relied upon as, a definitive determination of
                whether any particular communication, transaction, or
                interaction is legitimate or fraudulent.
              </p>
            </div>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Assumption of Risk and Limitation of Liability</h2>

            <div style={boxStyle}>
              <strong>
                YOU EXPRESSLY ACKNOWLEDGE, UNDERSTAND, AND AGREE THAT:
              </strong>
              <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
                <li>
                  Any advice, analysis, alert, recommendation, or educational
                  content provided by KinShield — whether generated by AI or
                  otherwise — that may result in you still falling victim to a
                  scam, fraud, phishing attack, social engineering scheme,
                  identity theft, or any other form of deception is{" "}
                  <strong>NOT</strong> the responsibility of KinShield, Reliable
                  IT Associates, LLC, or any of its officers, directors,
                  employees, agents, contractors, or affiliates, and they shall
                  not be liable for any resulting losses, damages, or harm of
                  any kind.
                </li>
                <li>
                  KinShield does not guarantee, warrant, or represent that its
                  Service will prevent you from falling victim to any scam,
                  fraud, or cyberattack, regardless of the quality,
                  thoroughness, or frequency of use of the Service.
                </li>
                <li>
                  You assume all risk associated with your use of or reliance on
                  KinShield's Service, AI-generated analyses, and educational
                  outputs.
                </li>
                <li>
                  You are solely responsible for independently verifying the
                  legitimacy of any communication, transaction, offer, request,
                  or interaction, and for making your own informed decisions.
                </li>
                <li>
                  KinShield shall not be liable for any financial losses,
                  identity theft, emotional distress, reputational harm, loss of
                  data, business interruption, or any other direct, indirect,
                  incidental, special, consequential, or punitive damages
                  arising from your use of or reliance on the Service.
                </li>
                <li>
                  These limitations apply regardless of the legal theory of the
                  claim, including breach of contract, tort (including
                  negligence), strict liability, or any other theory, and even
                  if KinShield has been advised of the possibility of such
                  damages.
                </li>
              </ul>
            </div>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Data Rights and Licensing</h2>

            <h3>Non-Exclusive License to Use and Store Data</h3>
            <p>
              By using KinShield, you grant KinShield and Reliable IT
              Associates, LLC a
              <strong>
                {" "}
                non-exclusive, worldwide, royalty-free, perpetual, irrevocable
                license
              </strong>{" "}
              to use, store, process, reproduce, analyze, and retain the
              information, content, and materials you provide to KinShield
              (including but not limited to emails, text messages, images,
              screenshots, social media content, URLs, phone numbers, documents,
              chatbot conversations, and any other user-submitted data) for the
              following purposes:
            </p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                Providing the Service, including AI-powered scam analysis and
                fraud detection
              </li>
              <li>
                Improving and enhancing KinShield's products, services, and AI
                models
              </li>
              <li>
                Conducting research, statistical analysis, and generating
                anonymized insights
              </li>
              <li>
                Generating anonymized and de-identified fraud intelligence for
                sharing with law enforcement, government entities, industry
                partners, and fraud prevention organizations
              </li>
              <li>
                Ensuring financial and legal compliance with all applicable laws
                and regulations
              </li>
              <li>Quality assurance, testing, and product development</li>
            </ul>
            <p>
              KinShield reserves this non-exclusive right to use and store the
              information you provide for effective analysis and financial and
              legal compliance with applicable laws. This right survives the
              termination of your account or subscription. You retain ownership
              of your original content, but the license granted herein is
              irrevocable.
            </p>

            <h3>Anonymized Fraud Intelligence Sharing</h3>
            <p>
              You consent to KinShield sharing anonymized and de-identified
              fraud intelligence derived from user-submitted scam data with law
              enforcement agencies, government entities, industry partners, and
              fraud prevention organizations. Personally identifiable victim
              information will never be shared; only scam-side artifacts (e.g.,
              fraudulent phone numbers, URLs, email addresses, and scam
              patterns) will be shared.
            </p>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Subscription and Service Details</h2>

            <h3>Service Level</h3>
            <p>☐ Individual Subscription</p>
            <p>☐ Family Subscription</p>
            <p>☐ Extended Family Subscription</p>

            <h3>Preferred Sophistication Level</h3>
            <p>☐ Level 1 (Basic - obvious indicators)</p>
            <p>☐ Level 2 (Realistic - professional appearance)</p>
            <p>☐ Level 3 (Highly sophisticated - maximum realism)</p>
            <p>☐ Mixed levels for comprehensive training</p>

            <h3>Communication Preferences</h3>
            <p>☐ Email simulations</p>
            <p>☐ Text message simulations</p>
            <p>☐ Voice call simulations</p>
            <p>
              ☐ Social media simulations (requires separate platform
              authorization)
            </p>

            <h3>SMS Communications Consent</h3>
            <p>
              By agreeing to this Consent Agreement, you consent to receive text
              messages for training and text simulation services. Message and
              data rates may apply. You can opt out at any time by replying
              STOP. Text message frequency varies based on your subscription
              level and training schedule.
            </p>
          </section>

          <hr style={hrStyle} />

          <section style={{ marginBottom: "30px" }}>
            <h2>Contact Information</h2>
            <p>
              If you have questions about this consent agreement or KinShield's
              services:
            </p>
            <ul style={{ textAlign: "left", paddingLeft: "20px" }}>
              <li>
                <strong>Company:</strong> Reliable IT Associates, LLC
              </li>
              <li>
                <strong>Email:</strong> info@kinprotect.com
              </li>
              <li>
                <strong>Phone:</strong> 301-237-7843
              </li>
            </ul>
            <p>
              <strong>Privacy Policy:</strong> Available at{" "}
              <button
                onClick={() => setLegalModalView("privacy")}
                style={linkButtonStyle}
              >
                www.kinprotect.com/privacy
              </button>
            </p>
            <p>
              <strong>Terms of Service:</strong> Available at{" "}
              <button
                onClick={() => setLegalModalView("terms")}
                style={linkButtonStyle}
              >
                www.kinprotect.com/terms
              </button>
            </p>
          </section>

          <div style={footerStyle}>
            <p>
              <em>
                This consent agreement is effective as of August 1, 2025 and was
                last updated on June 26, 2026. This agreement supersedes all
                previous versions. KinShield reserves the right to update this
                agreement with 30 days' notice to subscribers.
              </em>
            </p>
          </div>
        </div>
      </div>

      {/* Legal Modal */}
      {legalModalView && (
        <div
          onClick={() => setLegalModalView(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: isDark ? "#2a2a2a" : "white",
              borderRadius: "8px",
              maxWidth: "900px",
              width: "95%",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: isDark
                ? "0 4px 6px rgba(0, 0, 0, 0.3), 0 10px 20px rgba(0, 0, 0, 0.4)"
                : "0 4px 6px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.15)",
              color: isDark ? "#e0e0e0" : "inherit",
            }}
          >
            <button
              onClick={() => setLegalModalView(null)}
              style={{
                background: "none",
                border: "none",
                color: isDark ? "#90caf9" : "#1565c0",
                fontSize: "1rem",
                cursor: "pointer",
                padding: "1rem 1.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "0.5rem",
                fontWeight: 500,
                borderBottom: isDark ? "1px solid #444" : "1px solid #e0e0e0",
                width: "100%",
                textAlign: "right",
              }}
            >
              Close
            </button>
            <div style={{ flex: 1, overflowY: "auto", padding: 0 }}>
              {legalModalView === "privacy" && <PrivacyPolicy />}
              {legalModalView === "terms" && <TermsAndConditions />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserConsentAgreement;
